import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ImageB2c } from '../../model/image.model';

@Injectable()
export class GalleryService {
  private infiniteSlider = true;
  private imageSubject$ = new BehaviorSubject<ImageB2c | undefined>(undefined);
  private thumbnailsSubject$ = new BehaviorSubject<ImageB2c[]>([]);

  setThumbnails(images: ImageB2c[]): void {
    this.thumbnailsSubject$.next(images);
  }

  setInfiniteSlider(infinite: boolean): void {
    this.infiniteSlider = infinite;
  }

  get image(): ImageB2c | undefined {
    return this.image$.value;
  }

  get thumbnails(): ImageB2c[] {
    return this.thumbnails$.value;
  }

  get image$(): BehaviorSubject<ImageB2c | undefined> {
    return this.imageSubject$;
  }

  get thumbnails$(): BehaviorSubject<ImageB2c[]> {
    return this.thumbnailsSubject$;
  }

  /**
   * Select image from thumbnails list by index
   * @param index - desired index
   * @returns {ImageB2c | undefined} -- selected image
   */
  selectImageByIndex(index: number): ImageB2c | undefined {
    const newImage = this.thumbnails[index];
    if (newImage) {
      this.image$.next(newImage);
      return newImage;
    }
    return;
  }

  /**
   * Selects nex image from thumbnails list
   */
  next(): ImageB2c | undefined {
    if (!this.thumbnails.length) {
      return;
    }

    // Gets index of next image
    let index = this.image ? this.thumbnails.indexOf(this.image) : 0;
    if (!this.image) {
      index = 0;
    } else if (index >= this.thumbnails.length - 1) {
      if (!this.infiniteSlider) {
        return this.image;
      }
      index = 0;
    } else {
      index++;
    }

    // Sets new preview (actual image) and returns change
    return this.selectImageByIndex(index);
  }

  /**
   * Selects previous image from thumbnails list
   */
  previous(): ImageB2c | undefined {
    if (!this.thumbnails.length) {
      return;
    }

    // Gets index of previous image
    let index = this.image ? this.thumbnails.indexOf(this.image) : 0;
    if (!this.image) {
      index = this.thumbnails.length - 1;
    } else if (index <= 0) {
      if (!this.infiniteSlider) {
        return this.image;
      }
      index = this.thumbnails.length - 1;
    } else {
      index--;
    }

    // Sets new preview (actual image) and returns change
    return this.selectImageByIndex(index);
  }

  /**
   * Selects specific image from thumbnails list
   * @param image - desired image
   */
  selectImage(image: ImageB2c): ImageB2c | undefined {
    const index = this.thumbnails.indexOf(image);
    if (index < 0) {
      return;
    }

    // Sets new preview (actual image) and returns change
    return this.selectImageByIndex(index);
  }
}
